// src/modules/app-views/invoices/pages/Document/decorators/request.js
// *******************************************************************************
// REQUEST DECORATOR
// *******************************************************************************
// Transforms invoice data before sending to the backend API.
// Ensures proper data structure for items, payments, and fiscal data.
// *******************************************************************************

/**
 * Decorate invoice request data before sending to API
 * @param {Object} ctx - Context object containing data and form values
 * @returns {Object} Transformed data
 */
const invoicesDocumentDecorateRequest = ctx => {
  const { data, allValues } = ctx;

  if (!data) return data;

  const transformed = { ...data };

  // Ensure items array is properly structured
  if (transformed.items && Array.isArray(transformed.items)) {
    transformed.items = transformed.items.map((item, index) => ({
      ...item,
      itemNumber: index + 1,
    }));
  }

  // Ensure payment methods array is properly structured
  if (transformed.payment?.methods && Array.isArray(transformed.payment.methods)) {
    transformed.payment.methods = transformed.payment.methods.map(method => ({
      ...method,
      // Ensure amount is a number
      amount: Number(method.amount) || 0,
    }));
  }

  // Clean up undefined/null values from customer
  if (transformed.customer) {
    const cleanCustomer = {};
    Object.keys(transformed.customer).forEach(key => {
      if (transformed.customer[key] !== undefined && transformed.customer[key] !== null) {
        cleanCustomer[key] = transformed.customer[key];
      }
    });
    transformed.customer = cleanCustomer;
  }

  // Ensure totals are numbers
  if (transformed.totals) {
    const totalFields = [
      'productsTotal',
      'servicesTotal',
      'discount',
      'freight',
      'insurance',
      'otherExpenses',
      'icmsBase',
      'icmsValue',
      'pisValue',
      'cofinsValue',
      'invoiceTotal',
    ];

    totalFields.forEach(field => {
      if (transformed.totals[field] !== undefined) {
        transformed.totals[field] = Number(transformed.totals[field]) || 0;
      }
    });
  }

  // Handle Return or Complementary invoices reference documents
  // The frontend form keeps it in `referenceNfe` object (accessKey, documentNumber)
  // The backend schema requires an array of `referencedDocuments`
  if (transformed.referenceNfe && (transformed.referenceNfe.accessKey || transformed.referenceNfe.documentNumber)) {
    transformed.referencedDocuments = [{
      accessKey: transformed.referenceNfe.accessKey,
      documentNumber: transformed.referenceNfe.documentNumber
    }];
    delete transformed.referenceNfe; // remove form field representation
  }

  return transformed;
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default invoicesDocumentDecorateRequest;
